import {
  ApiMasterDataType,
  ChappiPieChartSegmentation,
  ChappiPieChartVariant,
  MetricId,
} from '../graphql/generated/graphql-sdk';
import {
  DataFields,
  DataFieldWithDataType,
  DataTypes,
  GroupBy,
  OverTimeTimeSelection,
  SingleValueTimeSelection,
  TimeSelectionType,
} from '../v2/types';

export type ChappiLineChartTimeSelection =
  | {
      __typename: 'CalendarYearMonthlyTimeSelection';
      startDate: any;
      endDate: any;
    }
  | {
      __typename: 'CalendarYearQuarterlyTimeSelection';
      startQuarter: {
        __typename?: 'Quarter';
        quarterOfYear: number;
        year: number;
      };
      endQuarter: {
        __typename?: 'Quarter';
        quarterOfYear: number;
        year: number;
      };
    }
  | {
      __typename: 'CalendarYearYearlyTimeSelection';
      startYear: number;
      endYear: number;
    }
  | {
      __typename: 'FinancialYearQuarterlyTimeSelection';
      startQuarter: {
        __typename?: 'Quarter';
        quarterOfYear: number;
        year: number;
      };
      endQuarter: {
        __typename?: 'Quarter';
        quarterOfYear: number;
        year: number;
      };
    }
  | {
      __typename: 'FinancialYearYearlyTimeSelection';
      startYear: number;
      endYear: number;
    };

export type ChappiLineChartSegmentationPart = {
  __typename?: 'ChappiLineChartSegmentation';
  datafieldAndDatatype: {
    __typename?: 'DatafieldAndDatatype';
    dataField: any;
    dataType: ApiMasterDataType;
  };
};
export type ChappiFilters = {
  sql: string;
} | null;

export type ChappiLineChartVisualization = {
  __typename: 'ChappiLineChartVisualization';
  lineChartMetrics: Array<MetricId>;
  lineChartTimeSelection: ChappiLineChartTimeSelection;
  lineChartSegmentation: ChappiLineChartSegmentationPart[];
  lineChartVariant:
    | 'ChappiLineChartVariantLineChart'
    | 'ChappiLineChartVariantAreaChart'
    | 'ChappiLineChartVariantStackedAreaChart';
  filters: ChappiFilters;
  responseForUser: string;
};

export type ChappyPieChartTimeSelection =
  | {
      __typename: 'SingleValueByCalendarMonthsTimeSelection';
      startDate: any;
      endDate: any;
    }
  | {
      __typename: 'SingleValueByCalendarYearsTimeSelection';
      startYear: number;
      endYear: number;
    }
  | {
      __typename: 'SingleValueByFinancialQuartersTimeSelection';
      startQuarter: {
        __typename?: 'Quarter';
        quarterOfYear: number;
        year: number;
      };
      endQuarter: {
        __typename?: 'Quarter';
        quarterOfYear: number;
        year: number;
      };
    }
  | {
      __typename: 'SingleValueByFinancialYearsTimeSelection';
      startYear: number;
      endYear: number;
    };
export type ChappiPieChartVisualization = {
  __typename?: 'ChappiPieChartVisualization';
  pieChartMetric: MetricId;
  pieChartSegmentation: ChappiPieChartSegmentation;
  pieChartTimeSelection: ChappyPieChartTimeSelection;
  pieChartVariant: ChappiPieChartVariant;
  filters: ChappiFilters;
  responseForUser: string;
};

export type ChappiKpiVisualization = {
  __typename: 'ChappiKpiVisualization';
  kpiMetric: Array<MetricId>;
  kpiTimeSelection: ChappyPieChartTimeSelection;
  kpiSegmentation: ChappiLineChartSegmentationPart[];
  alignment: 'ChappiKpiAlignmentColumn' | 'ChappiKpiAlignmentRow';
  filters: ChappiFilters;
  responseForUser: string;
};

export type ChappiOverTimeBarChartVisualization = {
  __typename: 'ChappiOverTimeBarChartVisualization';
  barChartMetrics: Array<MetricId>;
  barChartTimeSelection: ChappiLineChartTimeSelection;
  barChartSegmentation: ChappiLineChartSegmentationPart[];
  stack: boolean;
  relative: boolean;
  filters: ChappiFilters;
  responseForUser: string;
};

export type ChappiTableVisualization = {
  __typename: 'ChappiTableVisualization';
  tableMetrics: Array<MetricId>;
  tableTimeSelection: ChappiLineChartTimeSelection;
  tableSegmentation: ChappiLineChartSegmentationPart[];
  filters: ChappiFilters;
  responseForUser: string;
};

export type ChappiTimePeriodBarChartVisualization = {
  __typename: 'ChappiTimePeriodBarChartVisualization';
  barChartMetrics: Array<MetricId>;
  barChartTimeSelection: ChappyPieChartTimeSelection;
  barChartSegmentation: ChappiLineChartSegmentationPart[];
  stack: boolean;
  relative: boolean;
  filters: ChappiFilters;
  responseForUser: string;
};

export const convertChappiTimeSelection = (
  timeSelection: ChappiLineChartTimeSelection
): OverTimeTimeSelection => {
  switch (timeSelection.__typename) {
    case 'CalendarYearMonthlyTimeSelection':
      return {
        type: TimeSelectionType.CalendarYearMonthly,
        input: {
          start: timeSelection.startDate,
          end: timeSelection.endDate,
        },
      };
    case 'CalendarYearQuarterlyTimeSelection':
      return {
        type: TimeSelectionType.CalendarYearQuarterly,
        input: {
          start: {
            year: timeSelection.startQuarter.year,
            quarterOfYear: timeSelection.startQuarter.quarterOfYear,
          },
          end: {
            year: timeSelection.endQuarter.year,
            quarterOfYear: timeSelection.endQuarter.quarterOfYear,
          },
        },
      };
    case 'CalendarYearYearlyTimeSelection':
      return {
        type: TimeSelectionType.CalendarYearYearly,
        input: {
          start: timeSelection.startYear,
          end: timeSelection.endYear,
        },
      };
    case 'FinancialYearQuarterlyTimeSelection':
      return {
        type: TimeSelectionType.FinancialYearQuarterly,
        input: {
          start: {
            year: timeSelection.startQuarter.year,
            quarterOfYear: timeSelection.startQuarter.quarterOfYear,
          },
          end: {
            year: timeSelection.endQuarter.year,
            quarterOfYear: timeSelection.endQuarter.quarterOfYear,
          },
        },
      };
    case 'FinancialYearYearlyTimeSelection':
      return {
        type: TimeSelectionType.FinancialYearYearly,
        input: {
          start: timeSelection.startYear,
          end: timeSelection.endYear,
        },
      };
    default:
      throw new Error(
        `Unsupported time selection type: ${(timeSelection as any).__typename}`
      );
  }
};

export const convertChappiSegmentation = (
  segmentationPart: ChappiLineChartSegmentationPart | ChappiPieChartSegmentation
): GroupBy => {
  const dataFieldWithDataType: DataFieldWithDataType = {
    dataType: segmentationPart.datafieldAndDatatype
      .dataType as unknown as DataTypes, // TODO: fix conversion
    dataField: segmentationPart.datafieldAndDatatype.dataField as DataFields,
  };
  return {
    type: 'non-hierarchical',
    dataField: dataFieldWithDataType,
  };
};

export const convertChappiPieTimeSelection = (
  timeSelection: ChappyPieChartTimeSelection
): SingleValueTimeSelection => {
  switch (timeSelection.__typename) {
    case 'SingleValueByCalendarMonthsTimeSelection':
      return {
        type: TimeSelectionType.CalendarYearSingleValueByMonths,
        input: {
          start: timeSelection.startDate,
          end: timeSelection.endDate,
        },
      };
    case 'SingleValueByCalendarYearsTimeSelection':
      return {
        type: TimeSelectionType.CalendarYearSingleValueByYears,
        input: {
          start: timeSelection.startYear,
          end: timeSelection.endYear,
        },
      };
    case 'SingleValueByFinancialQuartersTimeSelection':
      return {
        type: TimeSelectionType.FinancialYearSingleValueByQuarters,
        input: {
          start: {
            year: timeSelection.startQuarter.year,
            quarterOfYear: timeSelection.startQuarter.quarterOfYear,
          },
          end: {
            year: timeSelection.endQuarter.year,
            quarterOfYear: timeSelection.endQuarter.quarterOfYear,
          },
        },
      };
    case 'SingleValueByFinancialYearsTimeSelection':
      return {
        type: TimeSelectionType.FinancialYearSingleValueByYears,
        input: {
          start: timeSelection.startYear,
          end: timeSelection.endYear,
        },
      };
    default:
      throw new Error(
        `Unsupported time selection type: ${(timeSelection as any).__typename}`
      );
  }
};

export type VisualizationBackend =
  | ChappiLineChartVisualization
  | ChappiPieChartVisualization
  | ChappiKpiVisualization
  | ChappiOverTimeBarChartVisualization
  | ChappiTableVisualization
  | ChappiTimePeriodBarChartVisualization;

export type ChappiCategorizationResponse = {
  __typename: 'ChappiCategorizationResponse';
  questionCategory: string;
};

export type ChappiResponse =
  | ChappiCategorizationResponse
  | VisualizationBackend
  | ChappiCauseInvestigationResponse
  | ChappiDataDiscrepancyResponse
  | ChappiExplainPreviousChartAnalyticsResponse
  | ChappiExplainPreviousSingleNumberAnalyticsResponse
  | ChappiExplanationOfCalculationOrDefinitionResponse
  | ChappiFindingSpecificEmployeesResponse
  | ChappiHowToUseChappiResponse
  | ChappiHowToUseThePanalytAppResponse
  | ChappiOtherOrUnknownResponse
  | ChappiPanalytAppGeneralQuestionOrProblemResponse
  | ChappiPanalytContractResponse
  | ChappiPredictiveAnalyticsForecastingResponse
  | ChappiProblemEncounteredResponse
  | ChappiRecommendationsSuggestionsResponse
  | ChappiSingleNumberAnalyticsResponse
  | ChappiVisualizationTypeResponse;

// Add Definitions for New Response Types
type ChappiCauseInvestigationResponse = {
  __typename: 'ChappiCauseInvestigationResponse';
  message: string;
};

type ChappiDataDiscrepancyResponse = {
  __typename: 'ChappiDataDiscrepancyResponse';
  message: string;
};

type ChappiExplainPreviousChartAnalyticsResponse = {
  __typename: 'ChappiExplainPreviousChartAnalyticsResponse';
  message: string;
};

type ChappiExplainPreviousSingleNumberAnalyticsResponse = {
  __typename: 'ChappiExplainPreviousSingleNumberAnalyticsResponse';
  message: string;
};

type ChappiExplanationOfCalculationOrDefinitionResponse = {
  __typename: 'ChappiExplanationOfCalculationOrDefinitionResponse';
  message: string;
};

type ChappiFindingSpecificEmployeesResponse = {
  __typename: 'ChappiFindingSpecificEmployeesResponse';
  message: string;
};

type ChappiHowToUseChappiResponse = {
  __typename: 'ChappiHowToUseChappiResponse';
  message: string;
};

type ChappiHowToUseThePanalytAppResponse = {
  __typename: 'ChappiHowToUseThePanalytAppResponse';
  message: string;
};

type ChappiOtherOrUnknownResponse = {
  __typename: 'ChappiOtherOrUnknownResponse';
  message: string;
};

type ChappiPanalytAppGeneralQuestionOrProblemResponse = {
  __typename: 'ChappiPanalytAppGeneralQuestionOrProblemResponse';
  message: string;
};

type ChappiPanalytContractResponse = {
  __typename: 'ChappiPanalytContractResponse';
  message: string;
};

type ChappiPredictiveAnalyticsForecastingResponse = {
  __typename: 'ChappiPredictiveAnalyticsForecastingResponse';
  message: string;
};

type ChappiProblemEncounteredResponse = {
  __typename: 'ChappiProblemEncounteredResponse';
  message: string;
};

type ChappiRecommendationsSuggestionsResponse = {
  __typename: 'ChappiRecommendationsSuggestionsResponse';
  message: string;
};

type ChappiSingleNumberAnalyticsResponse = {
  __typename: 'ChappiSingleNumberAnalyticsResponse';
  message: string;
};

type ChappiVisualizationTypeResponse = {
  __typename: 'ChappiVisualizationTypeResponse';
  visualizationType: string;
};
